import { computed } from "vue";
import { useThemeContext } from "@/core/composables/useThemeContext";
import { Logger } from "@/core/utilities";
import FeatureFlags from "@/core/utilities/featureFlags";

interface IPostalCodeRule {
  regex: string;
  example: string;
  prefix?: string;
}

type PostalCodeRulesType = Record<string, IPostalCodeRule>;

const SETTING_NAME = "PostalCodeFormatRules";
const MODULE_ID = "Thorlabs.Commerce";

export function usePostalCodeValidation() {
  const { themeContext } = useThemeContext();

  const postalCodeRules = computed<PostalCodeRulesType>(() => {
    const modules = themeContext.value?.storeSettings?.modules;
    if (!modules) {
      return {};
    }
    const rawJson = new FeatureFlags(modules).getValue(MODULE_ID, SETTING_NAME) as string | null;
    if (!rawJson) {
      return {};
    }
    try {
      return JSON.parse(rawJson) as PostalCodeRulesType;
    } catch (error) {
      Logger.error("usePostalCodeValidation: Failed to parse postal code rules", error);
      return {};
    }
  });

  const validatePostalCode = (code: string, countryCode: string): { valid: boolean; example?: string } => {
    if (!code || code.trim() === "") {
      return { valid: true };
    }
    const rule = postalCodeRules.value[countryCode];
    if (!rule) {
      return { valid: true };
    }
    try {
      const regex = new RegExp(rule.regex);
      if (!regex.test(code)) {
        return { valid: false, example: rule.example };
      }
    } catch (error) {
      Logger.error(`usePostalCodeValidation: Invalid regex for country "${countryCode}"`, error);
      return { valid: true };
    }
    return { valid: true };
  };

  const getPostalCodePrefix = (countryCode: string): string => {
    const rule = postalCodeRules.value[countryCode];
    return rule?.prefix ?? "";
  };

  return { validatePostalCode, getPostalCodePrefix };
}
